import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TabsModule } from 'primeng/tabs';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { CatalogOfferingsPanelComponent } from './catalog-offerings-panel.component';
import { CatalogCategoriesPanelComponent } from './catalog-categories-panel.component';
import { CatalogAttributeDefinitionsPanelComponent } from './catalog-attribute-definitions-panel.component';
import { CatalogProductsMediaPanelComponent } from './catalog-products-media-panel.component';

export type CatalogProductsHubTab = 'offerings' | 'categories' | 'attributes' | 'media';

@Component({
  standalone: true,
  selector: 'ain-catalog-products-hub',
  imports: [
    AinPageComponent,
    InfoHintComponent,
    TabsModule,
    CatalogOfferingsPanelComponent,
    CatalogCategoriesPanelComponent,
    CatalogAttributeDefinitionsPanelComponent,
    CatalogProductsMediaPanelComponent
  ],
  template: `
    <ain-page
      [eyebrow]="t('catalog.eyebrow')"
      [title]="t('catalog.products.hub.title')"
      [description]="t('catalog.products.hub.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'catalog.products.hub.help.title'"
        [descriptionKey]="'catalog.products.hub.help.description'"
        [bulletKeys]="[
          'catalog.products.hub.help.bullets.offerings',
          'catalog.products.hub.help.bullets.masterData',
          'catalog.products.hub.help.bullets.detail'
        ]" />

      @if (hubHint()) {
        <p class="hub-hint">{{ t(hubHint()!) }}</p>
      }

      <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
        <p-tablist>
          <p-tab value="offerings">{{ t('catalog.products.hub.tabs.offerings') }}</p-tab>
          <p-tab value="categories">{{ t('catalog.products.hub.tabs.categories') }}</p-tab>
          <p-tab value="attributes">{{ t('catalog.products.hub.tabs.attributes') }}</p-tab>
          <p-tab value="media">{{ t('catalog.products.hub.tabs.media') }}</p-tab>
        </p-tablist>
        <p-tabpanels>
          <p-tabpanel value="offerings">
            <ain-catalog-offerings-panel />
          </p-tabpanel>
          <p-tabpanel value="categories">
            <ain-catalog-categories-panel />
          </p-tabpanel>
          <p-tabpanel value="attributes">
            <ain-catalog-attribute-definitions-panel />
          </p-tabpanel>
          <p-tabpanel value="media">
            <ain-catalog-products-media-panel />
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>
    </ain-page>
  `,
  styles: [`
    :host ::ng-deep .p-tabpanels { padding-top: 12px; }
    .hub-hint { margin: 0 0 12px; padding: 10px 12px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; color: #1e3a8a; font-size: 14px; }
  `]
})
export class CatalogProductsHubComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly activeTab = signal<CatalogProductsHubTab>('offerings');
  readonly hubHint = signal<string | null>(null);

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const tab = params.get('tab');
      if (tab === 'categories' || tab === 'attributes' || tab === 'media' || tab === 'offerings') {
        this.activeTab.set(tab);
      } else if (!tab) {
        this.activeTab.set('offerings');
      }
      const hint = params.get('hubHint');
      this.hubHint.set(hint === 'profiles' ? 'catalog.products.hub.hint.profilesRedirect' : null);
    });
  }

  onTabChange(value: string | number | undefined): void {
    if (value === undefined || value === null) {
      return;
    }
    const key = String(value) as CatalogProductsHubTab;
    if (!['offerings', 'categories', 'attributes', 'media'].includes(key)) {
      return;
    }
    this.activeTab.set(key);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: key },
      queryParamsHandling: 'merge'
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
